import * as React from 'react'

import { FileText, SlidersVertical } from 'lucide-react'
import { makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  BicCompletionMode,
  CalendricalType,
  IInspectionOptions,
  IWorkflowStep,
  SitePermitStatus,
  WorkflowStepType,
} from '~/client/graph'
import SelectWrapper from '~/client/src/desktop/components/SelectWrapper/SelectWrapper'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import UserFieldPickerDialog from '~/client/src/shared/components/UserFieldPickerDialog'
import WeekdaysSelector from '~/client/src/shared/components/WeekdaysSelector/WeekdaysSelector'
import WorkflowStepNameLabel from '~/client/src/shared/components/WorkflowStepNameLabel'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import { allNotificationPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import { IWorkflowTypeAction } from '~/client/src/shared/models/WorkflowType'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import WorkflowAddStepModal from './components/WorkflowAddStepModal'
import WorkflowStepActions from './components/WorkflowStepActions'
import WorkflowStepBlock from './components/WorkflowStepBlock'
import WorkflowStepMenuModal from './components/WorkflowStepMenuModal'
import WorkflowStepRulesDialog from './components/WorkflowStepRulesDialog'
import WorkflowActionConfigurator from './WorkflowActionConfigurator/WorkflowActionConfigurator'
import WorkflowStepConfiguratorStore from './WorkflowStepConfigurator.store'

// localization: translated

interface IProps {
  workflowSteps: IWorkflowStep[]
  inspectionOptions: IInspectionOptions
  formTypeId: string
  isWorkflowChanged: boolean
  isAutoActivationEnabled: boolean
  isUpdating: boolean

  permitBallInCourtStore: PermitBallInCourtStore

  onAddStep(index: number, stepType: WorkflowStepType): void
  onReplaceStep(index: number, stepType: WorkflowStepType): void
  onRemoveStep(index: number): void
  onInspectionChange(inspectionOptions: IInspectionOptions): void
  onFormSectionClick(stepId: string): void
  onAutoActivationChange(): void
  onNotificationFieldsChange(
    stepId: string,
    fieldId: string,
    isSelected: boolean,
  ): void
  onNotificationFieldsBulkChange(
    stepId: string,
    fieldIds: string[],
    isSelected: boolean,
  ): void
  onNotificationUserChange(
    stepId: string,
    userId: string,
    isSelected: boolean,
  ): void
  onNotificationDiffChange(
    stepId: string,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
  ): void
  getNotificationUsers(notificationUserIds: string[]): User[]
  onActionsChange(stepId: string, actions: IWorkflowTypeAction[]): void

  removingRuleId: string
  newRuleId?: string
  getUsersFromAssignment(assignmentExpressions: string[]): User[]
  getStepRules(ruleIds: string[]): OperationRule[]
  onRemoveRuleClick(ruleId: string): void
  onRemoveRuleConfirm(): void
  onRemoveRuleCancel(): void
  onDuplicateRuleClick(ruleId: string): void
  onChangeUserForRule(
    rule: OperationRule,
    userId: string,
    isNew?: boolean,
  ): void
  onRuleExpressionChange(
    rule: OperationRule,
    locationAndCompExpression: string,
  ): void
  onAddNewRuleClick(stepId: string): void
  onFieldToggleForRule?(
    rule: OperationRule,
    fieldId: string,
    isSelected: boolean,
  ): void
  onFieldsToggleForRule?(
    rule: OperationRule,
    fieldIds: string[],
    isSelected: boolean,
  ): void
  onDiffCommitForRule?(
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ): void
  onBicCompletionModeChange(stepId: string, mode: BicCompletionMode): void
  onRenameRule(rule: OperationRule, name: string): void
  onCompanyDiffCommitForRule(
    rule: OperationRule,
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ): void

  projectDateStore?: ProjectDateStore
}

const isRecurringStep = (stepType: WorkflowStepType): boolean =>
  stepType === WorkflowStepType.RecurringInspection

@inject('projectDateStore')
@observer
export default class WorkflowStepConfigurator extends React.Component<IProps> {
  private readonly store: WorkflowStepConfiguratorStore

  public constructor(props: IProps) {
    super(props)

    this.store = new WorkflowStepConfiguratorStore(
      props.workflowSteps,
      props.inspectionOptions,
      props.isAutoActivationEnabled,
      props.projectDateStore,
      props.onInspectionChange,
    )
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    const { workflowSteps, inspectionOptions, isAutoActivationEnabled } =
      this.props
    if (
      workflowSteps.length !== prevProps.workflowSteps.length ||
      workflowSteps !== prevProps.workflowSteps ||
      inspectionOptions !== prevProps.inspectionOptions ||
      isAutoActivationEnabled !== prevProps.isAutoActivationEnabled
    ) {
      this.store.init(workflowSteps, inspectionOptions, isAutoActivationEnabled)
    }
  }

  public render() {
    const { workflowSteps, isLastStep, isAutomaticStartShown } = this.store
    return (
      <div className="col m10 workflow-configurator">
        {workflowSteps.map((step, idx) => (
          <React.Fragment key={step.id}>
            {this.renderStep(idx, step, isLastStep(idx))}
            {isAutomaticStartShown(idx, step.type) &&
              this.props.isAutoActivationEnabled &&
              this.renderAutomaticStep(step, idx)}
          </React.Fragment>
        ))}
        {this.renderNotificationDialog()}
      </div>
    )
  }

  private renderStep(
    stepIdx: number,
    step: IWorkflowStep,
    isLastStep: boolean,
  ): JSX.Element {
    const {
      isWorkflowChanged,
      isUpdating,
      formTypeId,
      onRemoveStep,
      onReplaceStep,
      getUsersFromAssignment,
      getStepRules,
      removingRuleId,
      newRuleId,
      onRemoveRuleClick,
      onRemoveRuleConfirm,
      onRemoveRuleCancel,
      onDuplicateRuleClick,
      onChangeUserForRule,
      onRuleExpressionChange,
      onAddNewRuleClick,
      onFieldToggleForRule,
      onFieldsToggleForRule,
      onDiffCommitForRule,
      onBicCompletionModeChange,
      onRenameRule,
      onCompanyDiffCommitForRule,
      onFormSectionClick,
      permitBallInCourtStore,
      workflowSteps: allWorkflowSteps,
    } = this.props
    const { workflowSteps, isRequestOrSubmissionStep, showNotificationDialog } =
      this.store

    const stepLevel = getWorkflowStepLevel(step.id, workflowSteps)
    const stepName = getWorkflowStepDisplayName(step.type, stepLevel)
    const isRecurring = isRecurringStep(step.type)

    return (
      <div className="relative no-select workflow-configurator-step">
        <div className="brada8 ba-light-grey overflow-hidden">
          <div className="row y-center bg-palette-brand-lightest px15 mb8 bb-light-grey h40">
            <WorkflowStepNameLabel
              stepPosition={stepIdx + 1}
              stepName={stepName}
            />
            <WorkflowStepMenuModal
              stepType={step.type}
              stepIdx={stepIdx}
              onRemoveStep={onRemoveStep}
              onReplaceStep={onReplaceStep}
              isRequestOrSubmitStep={isRequestOrSubmissionStep(step.type)}
            />
          </div>

          {!!stepIdx && !isRecurring && (
            <WorkflowStepBlock
              title={Localization.translator.bicUser}
              isBorderHidden
              tooltipContent={
                <>
                  {
                    Localization.translator.workflowConfTooltips
                      .bicBlockFirstLine
                  }
                  <br />
                  <br />
                  {
                    Localization.translator.workflowConfTooltips
                      .bicBlockSecondLine
                  }
                </>
              }
            >
              <WorkflowStepRulesDialog
                workflowStep={step}
                stepPosition={stepIdx + 1}
                stepName={stepName}
                formTypeId={formTypeId}
                isUpdating={isUpdating}
                allWorkflowSteps={allWorkflowSteps}
                removingRuleId={removingRuleId}
                newRuleId={newRuleId}
                getUsersFromAssignment={getUsersFromAssignment}
                getStepRules={getStepRules}
                onRemoveRuleClick={onRemoveRuleClick}
                onRemoveRuleConfirm={onRemoveRuleConfirm}
                onRemoveRuleCancel={onRemoveRuleCancel}
                onDuplicateRuleClick={onDuplicateRuleClick}
                onChangeUserForRule={onChangeUserForRule}
                onRuleExpressionChange={onRuleExpressionChange}
                onAddNewRuleClick={onAddNewRuleClick}
                onFieldToggleForRule={onFieldToggleForRule}
                onFieldsToggleForRule={onFieldsToggleForRule}
                onDiffCommitForRule={onDiffCommitForRule}
                onBicCompletionModeChange={onBicCompletionModeChange}
                onRenameRule={onRenameRule}
                onCompanyDiffCommitForRule={onCompanyDiffCommitForRule}
                permitBallInCourtStore={permitBallInCourtStore}
              />
            </WorkflowStepBlock>
          )}

          <WorkflowStepBlock
            title={Localization.translator.formSection}
            isBorderHidden
          >
            <FieldTrigger
              icon={<SlidersVertical />}
              disabled={isWorkflowChanged}
              onClick={() => onFormSectionClick(step.id)}
            >
              {step.fields.length ? (
                <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5">
                  <FileText />
                  <span className="tw:truncate">{stepName}</span>
                </span>
              ) : (
                Localization.translator.none
              )}
            </FieldTrigger>
          </WorkflowStepBlock>

          <WorkflowStepBlock title={Localization.translator.actions} isColumn>
            <WorkflowStepActions
              stepType={step.type}
              isLastStep={isLastStep}
              workflowStepLevel={stepLevel}
            />
          </WorkflowStepBlock>

          {!isRecurring && (
            <WorkflowStepBlock
              title={Localization.translator.notifiedUsers}
              isBorderHidden
            >
              <FieldTrigger
                icon={<SlidersVertical />}
                onClick={() => showNotificationDialog(step.id)}
              >
                {this.getNotificationSummary(step)}
              </FieldTrigger>
            </WorkflowStepBlock>
          )}

          {this.renderOptionsBlock(step, isLastStep)}
          {this.renderInspectionOptionsBlock(step.type)}
        </div>

        {this.renderLineAndAddStepModal(step, stepIdx, stepLevel, isLastStep)}
      </div>
    )
  }

  private renderOptionsBlock(
    step: IWorkflowStep,
    isLastStep: boolean,
  ): JSX.Element {
    if (isLastStep || !this.store.isAutoStartToggleShown(step.type)) {
      return null
    }

    return (
      <WorkflowStepBlock
        title={Localization.translator.options}
        isColumn
        isBorderHidden
      >
        <div className="tw:flex tw:h-8 tw:items-center tw:gap-2">
          <Switch
            checked={this.store.isAutoActivationEnabled}
            onCheckedChange={this.props.onAutoActivationChange}
          />
          <span className="tw:flex-1 text large line-extra-large">
            {Localization.translator.workflowConfDescr.automaticallyStart}
            <span className="text red large line-extra-large">*</span>
          </span>
        </div>
      </WorkflowStepBlock>
    )
  }

  private renderInspectionOptionsBlock(
    stepType: WorkflowStepType,
  ): JSX.Element {
    if (!isRecurringStep(stepType)) return null

    const {
      inspectionMaxNumber,
      inspectionFrequency,
      inspectionFrequencyType,
      inspectionFrequencyTypeOptions,
      inspectionDeadlineTime,
      deadlineTimeOptions,
      selectedDaysToRepeat,
      shouldShowRepeatOnDays,
      changeDayToRepeat,
    } = this.store

    return (
      <>
        <WorkflowStepBlock
          title={Localization.translator.frequency}
          isBorderHidden
        >
          <div className="row">
            <span className="mr5 text large nowrap no-grow">
              {Localization.translator.everyX_withoutNum(inspectionFrequency)}
            </span>
            <StruxhubInput
              className="inspection-frequency-input"
              isMinimalisticMode
              isHelperTextCondensed
              type="number"
              max={inspectionMaxNumber}
              min={1}
              value={inspectionFrequency.toString()}
              onChange={this.changeInspectionFrequency}
            />
            <div className="mr8 no-grow" />
            <SelectWrapper>
              <select
                title={Localization.translator.frequency}
                className="ba-light-grey brada4 pl8 pr24 pointer text large capitalize min-height32"
                value={inspectionFrequencyType}
                onChange={this.changeInspectionFrequencyType}
              >
                {inspectionFrequencyTypeOptions.map(({ value, name }) => (
                  <option
                    key={value}
                    className="text large capitalize"
                    value={value}
                  >
                    {name}
                  </option>
                ))}
              </select>
            </SelectWrapper>
          </div>
        </WorkflowStepBlock>

        {shouldShowRepeatOnDays && (
          <WorkflowStepBlock
            title={Localization.translator.repeatOn}
            isBorderHidden
          >
            <WeekdaysSelector
              className="py2"
              selectedDayNumbers={selectedDaysToRepeat}
              onClick={changeDayToRepeat}
            />
          </WorkflowStepBlock>
        )}

        <WorkflowStepBlock
          title={Localization.translator.deadline}
          isBorderHidden
        >
          <SelectWrapper>
            <select
              title={Localization.translator.deadline}
              className="ba-light-grey brada4 pl8 pr24 pointer min-height32"
              value={inspectionDeadlineTime.toString()}
              onChange={this.changeInspectionDeadline}
            >
              {deadlineTimeOptions.map(({ value, name }) => (
                <option key={value} className="text large" value={value}>
                  {name}
                </option>
              ))}
            </select>
          </SelectWrapper>
        </WorkflowStepBlock>
      </>
    )
  }

  private renderLineAndAddStepModal(
    step: IWorkflowStep,
    stepIdx: number,
    stepLevel: number,
    isLastStep: boolean,
    lineStatus?: SitePermitStatus,
  ): JSX.Element {
    const { isAddStepBtnShown, getStepResultStatus } = this.store
    const shouldShowStatus = lineStatus || !isRecurringStep(step.type)

    return (
      <div
        className={classList({
          relative: true,
          'workflow-configurator-step-line': !isLastStep,
          'workflow-configurator-step-last-line': isLastStep,
        })}
      >
        <div className="min-height50 absolute-block-x-center relative">
          <WorkflowActionConfigurator
            stepId={step.id}
            actions={step.actions}
            onActionsChange={actions =>
              this.props.onActionsChange(step.id, actions)
            }
            stepsConfiguratorStore={this.store}
          />
        </div>
        {isAddStepBtnShown(step.type) && (
          <WorkflowAddStepModal
            stepType={step.type}
            stepIdx={stepIdx}
            onAddStep={this.props.onAddStep}
            store={this.store}
          />
        )}
        <div
          className={classList({
            'full-height full-width col y-center x-center': true,
            'y-end': isLastStep,
          })}
        >
          <div className="absolute-top full-height z-index-1 bl-light-grey" />
          {shouldShowStatus && (
            <div className="z-index-3 bg-white">
              <StatusBadge
                status={
                  lineStatus || getStepResultStatus(isLastStep, step.type)
                }
                workflowStepLevel={isLastStep ? 0 : stepLevel}
                isLate={false}
              />
            </div>
          )}
        </div>
        {!isLastStep && <div className="min-height50" />}
      </div>
    )
  }

  private renderAutomaticStep(
    step: IWorkflowStep,
    stepIdx: number,
  ): JSX.Element {
    const { onAddStep, onRemoveStep, onReplaceStep } = this.props

    return (
      <div className="relative no-select workflow-configurator-step">
        <div className="brada8 ba-light-grey overflow-hidden">
          <div className="row y-center bg-palette-brand-lightest px15 mb8 bb-light-grey h40">
            <span className="row text large bold line-extra-large capitalize">
              {Localization.translator.automaticStart}
            </span>
            <WorkflowStepMenuModal
              stepType={step.type}
              stepIdx={stepIdx}
              onRemoveStep={onRemoveStep}
              onReplaceStep={onReplaceStep}
              isAutomaticStep
              isRequestOrSubmitStep={false}
              onAddStep={onAddStep}
            />
          </div>
          <div className="row y-start px15 mb8 bg-white">
            <span className="row py6 text large line-extra-large">
              {Localization.translator.workflowConfDescr.workflowStartsOnDate}
            </span>
          </div>
        </div>

        {this.renderLineAndAddStepModal(
          step,
          stepIdx,
          0,
          false,
          SitePermitStatus.Active,
        )}
      </div>
    )
  }

  private changeInspectionDeadline = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.store.changeDeadlineTime(+event?.target?.value)
  }

  private changeInspectionFrequency = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.store.changeFrequency(event.target.valueAsNumber || 1)
  }

  private changeInspectionFrequencyType = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.store.changeFrequencyType(event.target.value as CalendricalType)
  }

  private renderNotificationDialog(): JSX.Element {
    const {
      workflowSteps: allWorkflowSteps,
      onNotificationFieldsChange,
      onNotificationFieldsBulkChange,
      onNotificationUserChange,
      onNotificationDiffChange,
      getNotificationUsers,
    } = this.props
    const {
      shouldShowNotificationDialog,
      hideNotificationDialog,
      notificationDialogStepId,
      workflowSteps,
    } = this.store

    const step = workflowSteps.find(s => s.id === notificationDialogStepId)
    if (!step) {
      return null
    }

    const notificationUsers = getNotificationUsers(
      step.notificationUserIds || [],
    )

    return (
      <UserFieldPickerDialog
        isOpen={shouldShowNotificationDialog}
        title={Localization.translator.notifiedUsers}
        selectedUsers={notificationUsers}
        onUserAdd={(userId: string) =>
          onNotificationUserChange(notificationDialogStepId, userId, true)
        }
        onUserRemove={(userId: string) =>
          onNotificationUserChange(notificationDialogStepId, userId, false)
        }
        onDiffCommit={(
          addedUserIds: string[],
          removedUserIds: string[],
          addedFieldIds: string[],
          removedFieldIds: string[],
        ) =>
          onNotificationDiffChange(
            notificationDialogStepId,
            addedUserIds,
            removedUserIds,
            addedFieldIds,
            removedFieldIds,
          )
        }
        allWorkflowSteps={allWorkflowSteps}
        selectedFieldIds={step.notificationFieldIds || []}
        allowedFieldTypes={allNotificationPermitFieldTypes}
        onFieldToggle={(fieldId: string, isSelected: boolean) =>
          onNotificationFieldsChange(
            notificationDialogStepId,
            fieldId,
            isSelected,
          )
        }
        onFieldsToggle={(fieldIds: string[], isSelected: boolean) =>
          onNotificationFieldsBulkChange(
            notificationDialogStepId,
            fieldIds,
            isSelected,
          )
        }
        onClose={hideNotificationDialog}
      />
    )
  }

  private getNotificationSummary(step: IWorkflowStep): string {
    const userCount = (step.notificationUserIds || []).length
    const fieldCount = (step.notificationFieldIds || []).length
    const totalCount = userCount + fieldCount

    if (totalCount === 0) {
      return Localization.translator.none
    }

    return `${totalCount} ${Localization.translator.selected}`
  }
}
